import { useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { parseError } from '@/lib/errors'
import { useDeleteQaThread, useQaEmailThread, useQaEmailThreads } from '@/models/qaEmail'
import { EmailThreadList } from '../../Internal/Emails/EmailThreadList'
import { EmailThreadView } from '../../Internal/Emails/EmailThreadView'
import { QaComposeDialog } from './QaComposeDialog'
import { QaReplyComposer } from './QaReplyComposer'

/**
 * The QA inbox: virtual mail only. Threads on the left, the selected thread
 * plus a reply-as-the-customer box on the right.
 */
export function QaEmails() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [composeOpen, setComposeOpen] = useState(false)
  const q = searchParams.get('q') ?? ''
  const parsedPage = Number(searchParams.get('page'))
  const page = Number.isInteger(parsedPage) ? Math.max(1, parsedPage) : 1
  const threadKey = searchParams.get('thread')

  function update(changes: Record<string, string | undefined>) {
    const next = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(changes)) {
      if (value) next.set(key, value)
      else next.delete(key)
    }
    setSearchParams(next)
  }

  const { data, isLoading } = useQaEmailThreads(q, page)
  const { data: thread, isLoading: threadLoading } = useQaEmailThread(threadKey)
  const { mutateAsync: deleteThread, isPending: isDeleting } = useDeleteQaThread()

  async function onDelete() {
    if (!threadKey) return
    try {
      await deleteThread(threadKey)
      toast.success('Thread deleted')
      update({ thread: undefined })
    } catch (err) {
      toast.error(parseError(err))
    }
  }

  return (
    <>
      <Helmet>
        <title>QA Emails</title>
      </Helmet>
      {/* Not flex-1: its 0% basis resolves to content height in the min-height shell, so the panes would grow. */}
      <div className="flex min-h-96 w-full grow basis-0 flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-2xl font-semibold">QA Emails</h1>
          <div className="flex items-center gap-2">
            <Input
              value={q}
              placeholder="Search"
              onChange={(e) => update({ q: e.target.value || undefined, page: undefined })}
            />
            <Button onClick={() => setComposeOpen(true)}>Compose</Button>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-[minmax(20rem,26rem)_1fr] gap-4">
          <EmailThreadList
            page={data}
            isLoading={isLoading}
            pageNumber={page}
            selectedKey={threadKey}
            onSelect={(key) => update({ thread: key })}
            onPage={(next) => update({ page: next > 1 ? String(next) : undefined })}
          />
          {/* Outside EmailThreadView so its 4s poll can't eat a half-typed reply. */}
          <div className="flex min-h-0 flex-col gap-3">
            <div className="min-h-0 flex-1">
              <EmailThreadView threadKey={threadKey} thread={thread} isLoading={threadLoading} />
            </div>
            {thread && (
              <>
                <QaReplyComposer thread={thread} />
                {/* Deleting is how an operator releases a record from QA capture. */}
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onDelete}
                    loading={isDeleting ? 'Deleting…' : undefined}
                  >
                    Delete thread
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <QaComposeDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        onSent={(key) => update({ thread: key ?? undefined })}
      />
    </>
  )
}
